import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface TrialBalanceFilters {
  subsidiary_ids?: (number | string)[]; // Optional: if not provided, generates consolidated trial balance
  fiscal_period_id: number | string; // Required by backend
  parent_company_id?: number | string; // For consolidated reports (future feature)
  limit?: number;
  offset?: number;
  search?: string;
  exclude_void?: boolean;
}

// Enhanced filters for PTD/YTD and comparison
export interface TrialBalanceEnhancedFilters {
  subsidiary_ids?: (number | string)[]; // Optional: if not provided, generates consolidated trial balance
  report_type: 'PTD' | 'YTD'; // Period-to-Date or Year-to-Date
  fiscal_period_id?: number | string; // Use period or date range
  start_date?: string; // YYYY-MM-DD - alternative to fiscal_period_id
  end_date?: string; // YYYY-MM-DD
  comparison_period_id?: number | string; // For comparison (specific period ID)
  comparison_type?: string; // DEPRECATED: For predefined comparison types (e.g., "LFY", "TPOLFY", "LP")
  comparison_mode?: string; // New: "MM", "MY", "YY"
  comparison_start_date?: string; // For date-based comparison
  comparison_end_date?: string;
  parent_company_id?: number | string;
  limit?: number;
  offset?: number;
  search?: string;
  exclude_void?: boolean;
}

export interface TrialBalanceAccount {
  account_id: number;
  account_code: string;
  account_name: string;
  account_type: string;
  beginning_debit: number;
  beginning_credit: number;
  period_debit: number;
  period_credit: number;
  ending_debit: number;
  ending_credit: number;
  net_balance: number;
}

export interface TrialBalancePeriodData {
  debit: number;
  credit: number;
  net_balance: number;
}

export interface TrialBalanceColumn {
  key: string;
  label: string;
  type: string;
}

// Enhanced account with comparison data
export interface TrialBalanceEnhancedAccount extends TrialBalanceAccount {
  // Dynamic Period Data
  period_data?: { [key: string]: TrialBalancePeriodData };

  // Comparison period data (Legacy)
  comparison_beginning_debit?: number;
  comparison_beginning_credit?: number;
  comparison_period_debit?: number;
  comparison_period_credit?: number;
  comparison_ending_debit?: number;
  comparison_ending_credit?: number;
  comparison_net_balance?: number;
  // Variance calculations
  variance_amount?: number;
  variance_percentage?: number;
}

export interface TrialBalanceApiResponse {
  status: string;
  message: string;
  data: {
    subsidiary_ids: number[];
    fiscal_period_id: number;
    accounts: TrialBalanceAccount[];
    total_beginning_debit: number;
    total_beginning_credit: number;
    total_period_debit: number;
    total_period_credit: number;
    total_ending_debit: number;
    total_ending_credit: number;
    is_balanced: boolean;
  };
}

export interface TrialBalanceEnhancedResponse {
  status: string;
  message: string;
  data: {
    subsidiary_ids: number[];
    subsidiary_name: string;
    report_type: string;
    fiscal_period_id?: number;
    fiscal_period_name?: string;
    start_date?: string;
    end_date?: string;
    
    // Dynamic Columns
    columns?: TrialBalanceColumn[];
    comparison_mode?: string;

    comparison_period_id?: number;
    comparison_period_name?: string;
    comparison_start_date?: string;
    comparison_end_date?: string;
    accounts: TrialBalanceEnhancedAccount[];
    // Current period totals
    total_beginning_debit: number;
    total_beginning_credit: number;
    total_period_debit: number;
    total_period_credit: number;
    total_ending_debit: number;
    total_ending_credit: number;
    // Comparison period totals (if comparison requested)
    comparison_total_beginning_debit?: number;
    comparison_total_beginning_credit?: number;
    comparison_total_period_debit?: number;
    comparison_total_period_credit?: number;
    comparison_total_ending_debit?: number;
    comparison_total_ending_credit?: number;
    is_balanced: boolean;
    has_comparison: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class TrialBalanceService {
  private readonly API_BASE_URL = environment.accountingUrl;
  private http = inject(HttpClient);

  /**
   * Get trial balance with optional filters
   * Endpoint: GET /api/v1/financial-statements/trial-balance
   * 
   * Required: subsidiary_id and fiscal_period_id
   */
  getTrialBalance(filters: TrialBalanceFilters): Observable<TrialBalanceApiResponse> {
    let params = new HttpParams();

    if (filters.subsidiary_ids && filters.subsidiary_ids.length > 0) {
      params = params.set('subsidiary_ids', filters.subsidiary_ids.join(','));
    }

    // Required: fiscal_period_id
    if (filters.fiscal_period_id !== undefined && filters.fiscal_period_id !== null) {
      params = params.set('fiscal_period_id', filters.fiscal_period_id.toString());
    }

    // Optional: parent_company_id for consolidated reports (future feature)
    if (filters.parent_company_id !== undefined && filters.parent_company_id !== null) {
      params = params.set('parent_company_id', filters.parent_company_id.toString());
    }

    if (filters.limit !== undefined) {
      params = params.set('limit', filters.limit.toString());
    }

    if (filters.offset !== undefined) {
      params = params.set('offset', filters.offset.toString());
    }

    if (filters.search) {
      params = params.set('search', filters.search);
    }

    if (filters.exclude_void) {
      params = params.set('exclude_void', 'true');
    }

    return this.http.get<TrialBalanceApiResponse>(`${this.API_BASE_URL}/api/v1/financial-statements/trial-balance-gl`, {
      params,
      withCredentials: true
    });
  }

  /**
   * Get enhanced trial balance with PTD/YTD and comparison features
   * Endpoint: GET /api/v1/financial-statements/trial-balance-enhanced
   */
  getTrialBalanceEnhanced(filters: TrialBalanceEnhancedFilters): Observable<TrialBalanceEnhancedResponse> {
    let params = new HttpParams();

    if (filters.subsidiary_ids && filters.subsidiary_ids.length > 0) {
      params = params.set('subsidiary_ids', filters.subsidiary_ids.join(','));
    }

    // Required: report_type (PTD or YTD)
    params = params.set('report_type', filters.report_type);

    // Option 1: Use fiscal_period_id
    if (filters.fiscal_period_id !== undefined && filters.fiscal_period_id !== null) {
      params = params.set('fiscal_period_id', filters.fiscal_period_id.toString());
    }

    // Option 2: Use date range
    if (filters.start_date) {
      params = params.set('start_date', filters.start_date);
    }
    if (filters.end_date) {
      params = params.set('end_date', filters.end_date);
    }

    // Comparison period (optional - by specific period ID)
    if (filters.comparison_period_id !== undefined && filters.comparison_period_id !== null) {
      params = params.set('comparison_period_id', filters.comparison_period_id.toString());
    }
    // Comparison type (optional - predefined comparison like "LFY", "TPOLFY", etc.)
    if (filters.comparison_type) {
      params = params.set('comparison_type', filters.comparison_type);
    }
    // Comparison mode (new)
    if (filters.comparison_mode) {
      params = params.set('comparison_mode', filters.comparison_mode);
    }
    if (filters.comparison_start_date) {
      params = params.set('comparison_start_date', filters.comparison_start_date);
    }
    if (filters.comparison_end_date) {
      params = params.set('comparison_end_date', filters.comparison_end_date);
    }

    // Optional: parent_company_id for consolidated reports
    if (filters.parent_company_id !== undefined && filters.parent_company_id !== null) {
      params = params.set('parent_company_id', filters.parent_company_id.toString());
    }

    if (filters.limit !== undefined) {
      params = params.set('limit', filters.limit.toString());
    }

    if (filters.offset !== undefined) {
      params = params.set('offset', filters.offset.toString());
    }

    if (filters.search) {
      params = params.set('search', filters.search);
    }

    if (filters.exclude_void) {
      params = params.set('exclude_void', 'true');
    }

    return this.http.get<TrialBalanceEnhancedResponse>(`${this.API_BASE_URL}/api/v1/financial-statements/trial-balance-enhanced`, {
      params,
      withCredentials: true
    });
  }
}

